import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { ArticlePhoto } from "@/mk2/apps/wiki/containers/ArticlePhoto/ArticlePhoto";
import { articlePhotoFetchTrigger } from "@/mk2/apps/wiki/containers/ArticlePhoto/ArticlePhoto.actions";
import { articlesPhotosReducer } from "@/mk2/apps/wiki/containers/ArticlePhoto/ArticlePhoto.reducers";
import articlesPhotosSaga from "@/mk2/apps/wiki/containers/ArticlePhoto/ArticlePhoto.sagas";
import { geArticlePhotosState } from "@/mk2/apps/wiki/containers/ArticlePhoto/ArticlePhoto.selectors";
import { isArticleOfReviewsCategory } from "@/mk2/apps/wiki/helpers";
import { wikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { AppName } from "@/mk2/containers/Application/Application";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps, RightContentSide } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { MenuSelection, PhotoEntity, WikiArticleEntity, WikiArticleSchema } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import { SEO_TITLE_WIKI_article } from "@/mk/autogenerated/translation";

export interface OwnProps {}

export interface StateProps {
    photoId: number;
    errorMessage: string;
    loadingState: LoadingState;
    article: WikiArticleEntity;
    articlePhoto: PhotoEntity;
}

export interface DispatchProps {
    onLoad(articleSlug: string, categorySlug: string, photoId: number);
}

export type RouteProps = PageRouteProps<{
    categorySlug: string;
    articleSlug: string;
    photoId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticlePhotoPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const { routeParams } = ownProps;
            const categorySlug = routeParams.categorySlug;
            const articleSlug = routeParams.articleSlug;
            const photoId = parseInt(routeParams.photoId, 10);

            const photosState = geArticlePhotosState(state, categorySlug, articleSlug);
            const article: WikiArticleEntity = getDenormalizedEntity<WikiArticleEntity>(
                state,
                WikiArticleSchema,
                photosState.articleId,
            );
            const articlePhoto = article ? article.photos.find((p) => p.id === photoId) : null;

            const loadingState =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.loadingState
                    : articlePhoto
                      ? LoadingState.SUCCESS
                      : LoadingState.FAILURE;
            const errorMessage =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.errorMessage
                    : articlePhoto
                      ? null
                      : "NOT FOUND";

            return {
                article,
                articlePhoto,
                photoId,
                loadingState,
                errorMessage,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: articlePhotoFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { article } = props;
        if (article) {
            // custom napisany SEO title, alebo to iste ako nadpis clanku
            return (
                article.seoTitle || interpolate(SEO_TITLE_WIKI_article, { article: article.title })
            );
        } else {
            return null;
        }
    }

    public getMetaDescription(props: Props): string {
        const { article } = props;
        if (article) {
            // custom napisany SEO description, alebo to iste ako prvy odstavec clanku
            return article.seoDescription || getFirstParagraphsPlaintext(article.bodyAST);
        } else {
            // pouzi defaultny description v Page
            return null;
        }
    }

    public getMetaFaqRichSnippet(props: Props): string {
        const { article } = props;
        return article?.seoFaqRichSnippet;
    }

    public render(props: Props): false | JSX.Element {
        return <ArticlePhoto {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public getHeaderTitle(props: Props): string {
        const { article } = props;
        return article ? interpolate(SEO_TITLE_WIKI_article, { article: article.title }) : null;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const {
            routeParams: { categorySlug, articleSlug },
        } = props;
        return url(wikiArticleUrl, { categorySlug, articleSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { article } = props;
        if (article && !article.disabledAds && !isArticleOfReviewsCategory(article.category.slug)) {
            return {
                zone: "Wiki - Halfpage Sticky (Desktop)",
            };
        }
        return null;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: props.article ? props.article.slug : null,
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.wiki.articlesPhotos", articlesPhotosReducer);
        store.injectSaga("containers.wiki.articlesPhotos", articlesPhotosSaga);
    }
}
